import React, { useState, useRef, useEffect, forwardRef } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";

import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Grid,
  InputAdornment,
  IconButton,
  Toolbar,
  TextField,
  Typography,
  Button,
} from "@mui/material";

import { Visibility, VisibilityOff } from "@mui/icons-material";

import UsPhoneField, { toPhoneDigits, toE164 } from "../shared/UsPhoneField";

//hooks
import { useTenant } from "../../hooks/useTenant";
import { useAuth } from "../../context/AuthContext";
import { IMaskInput, useIMask } from "react-imask";
import { signupRequest, resendVerificationEmailRequest } from "../../api/api";

//components
import CustomAppBar from "../customAppBar";
import LoadingScreen from "../../shared_modules/loadingScreen";

import myVideo from "../../images/bellosvideo.mp4";

const PhoneMaskInput = forwardRef(function PhoneMaskInput(
  { onChange, ...props },
  ref,
) {
  return (
    <IMaskInput
      {...props}
      mask="(000) 000-0000"
      inputRef={ref}
      onAccept={(value) => onChange({ target: { value } })}
      placeholder="Enter your phone number"
    />
  );
});

const SignUp = () => {
  const navigate = useNavigate();
  const { tenant, status } = useTenant();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);

  //resend
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const resendTimerRef = useRef(null);

  // Clear the cooldown interval on unmount so it can't fire setState afterward
  useEffect(() => {
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    type: "",
  });

  const { ref, value } = useIMask({ mask: "(000) 000-0000" });

  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");

  if (status === "loading") {
    return <LoadingScreen />;
  }

  //if the user is already logged in, navigate to dashboard
  //if (session && user) return <Navigate to="/dashboard" replace />;

  const handleSignUp = async () => {
    setShowAlert({ show: false, message: "", type: "" });

    // ── Client-side validation (mirrors the backend rules so users get
    //    instant feedback instead of a server round-trip) ──
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!firstName.trim() || !lastName.trim()) {
      setShowAlert({
        show: true,
        message: "Please enter your first and last name.",
        type: "error",
      });
      return;
    }
    if (toPhoneDigits(phone).length !== 10) {
      setShowAlert({
        show: true,
        message: "Please enter a valid 10-digit phone number.",
        type: "error",
      });
      return;
    }
    if (!emailRegex.test(email.trim())) {
      setShowAlert({
        show: true,
        message: "Please enter a valid email address.",
        type: "error",
      });
      return;
    }
    if (password.length < 8) {
      setShowAlert({
        show: true,
        message: "Password must be at least 8 characters.",
        type: "error",
      });
      return;
    }
    if (password !== confirmPassword) {
      setShowAlert({
        show: true,
        message: "Passwords do not match.",
        type: "error",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await signupRequest(
        firstName,
        lastName,
        email,
        password,
        confirmPassword,
        phone,
        inviteToken,
      );

      handleResendTimer();

      setShowAlert({
        show: true,
        message: (
          <>
            We sent a confirmation link to <strong>{email}</strong>. Click the
            link to activate your account.
            <br />
            <br />
            <span style={{ opacity: 0.75 }}>
              Don't see it? Check your <strong>spam</strong> or{" "}
              <strong>junk</strong> folder.
            </span>
          </>
        ),
        type: "success",
      });
    } catch (err) {
      // A returning customer (including a past guest) who already has an
      // account gets a clearer nudge to log in instead of a raw error.
      const alreadyExists = /already exists/i.test(err.message || "");

      setShowAlert({
        show: true,
        message: alreadyExists ? (
          <>
            An account with this email already exists.{" "}
            <span
              onClick={() => navigate("/login")}
              style={{ fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
            >
              Log in instead
            </span>{" "}
            — or reset your password if you've forgotten it.
          </>
        ) : (
          err.message
        ),
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  async function handleResend() {
    setResendLoading(true);

    try {
      await resendVerificationEmailRequest(email);

      handleResendTimer();

      setShowAlert({
        show: true,
        message: "Verification email sent again.",
        type: "success",
      });
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setResendLoading(false);
    }
  }

  function handleResendTimer() {
    // Start 60 second cooldown so they can't spam the button
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    setResendCooldown(true);
    setCountdown(60);

    resendTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(resendTimerRef.current);
          resendTimerRef.current = null;
          setResendCooldown(false);
          return 60;
        }
        return prev - 1;
      });
    }, 1000);
  }

  return (
    <>
      <CustomAppBar tenant={tenant} showLoginButton={false} />

      <Grid
        container
        sx={{ height: "calc(100vh - 64px)", minHeight: "500px", mt: "64px" }}
      >
        <Grid size={{ xs: 12, lg: 7 }}>
          <Box
            sx={{
              backgroundColor: "background.default",
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", lg: "center" },
              flexDirection: "column",
              height: "100%",
              padding: { xs: "30px 20px", lg: "30px 50px" },
            }}
          >
            <Typography variant="h3">Create an Account</Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 1,
                mb: 4,
                fontWeight: 300,
              }}
            >
              Join now to access all the features and benefits of our platform.
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "white",
                border: "1px solid",
                borderColor: "customDivider",
                padding: "30px",
                borderRadius: "24px",
                width: { xs: "100%", sm: "500px" },
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              }}
            >
              {showAlert.show === true && (
                <Alert
                  sx={{ mb: showAlert.type === "success" ? 0 : 2 }}
                  severity={showAlert.type}
                >
                  {showAlert.message}
                </Alert>
              )}

              {showAlert.show === true && showAlert.type === "success" && (
                <Box sx={{ display: "flex", width: "100%" }}>
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={resendLoading || resendCooldown}
                    sx={{ mt: 5, width: "100%" }}
                    onClick={() => {
                      handleResend();
                    }}
                  >
                    {resendCooldown
                      ? `Resend Email (${countdown}s)`
                      : resendLoading
                        ? "Sending..."
                        : "Resend confirmation email"}
                  </Button>
                </Box>
              )}

              {showAlert.type !== "success" && (
                <>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "text.primary",
                          fontWeight: 300,
                          pb: 1,
                        }}
                      >
                        First Name
                      </Typography>
                      <TextField
                        variant="outlined"
                        disabled={isSubmitting}
                        fullWidth
                        label=""
                        placeholder="Enter your first name"
                        value={firstName}
                        onChange={(e) => {
                          setFirstName(e.target.value);
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "text.primary",
                          fontWeight: 300,
                          pb: 1,
                        }}
                      >
                        Last Name
                      </Typography>
                      <TextField
                        variant="outlined"
                        disabled={isSubmitting}
                        fullWidth
                        label=""
                        placeholder="Enter your last name"
                        value={lastName}
                        onChange={(e) => {
                          setLastName(e.target.value);
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      fontWeight: 300,
                      pb: 1,
                      mt: 3,
                    }}
                  >
                    Phone
                  </Typography>

                  <UsPhoneField
                    value={toPhoneDigits(phone)}
                    onChange={(digits) => setPhone(toE164(digits))}
                    disabled={isSubmitting}
                  />

                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      fontWeight: 300,
                      pb: 1,
                      mt: 3,
                    }}
                  >
                    Email
                  </Typography>
                  <TextField
                    variant="outlined"
                    disabled={isSubmitting}
                    label=""
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                  />

                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      fontWeight: 300,
                      mt: 3,
                      pb: 1,
                    }}
                  >
                    Password
                  </Typography>
                  <TextField
                    variant="outlined"
                    disabled={isSubmitting}
                    type={showPassword ? "text" : "password"}
                    label=""
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              disabled={isSubmitting}
                              aria-label={
                                showPassword
                                  ? "hide the password"
                                  : "display the password"
                              }
                              onClick={() => setShowPassword((show) => !show)}
                              //onMouseDown={handleMouseDownPassword}
                              //onMouseUp={handleMouseUpPassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff sx={{ fontSize: "20px" }} />
                              ) : (
                                <Visibility sx={{ fontSize: "20px" }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      fontWeight: 300,
                      mt: 3,
                      pb: 1,
                    }}
                  >
                    Confirm Password
                  </Typography>
                  <TextField
                    variant="outlined"
                    disabled={isSubmitting}
                    type={showPassword ? "text" : "password"}
                    label=""
                    placeholder="Enter your password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              disabled={isSubmitting}
                              aria-label={
                                showPassword
                                  ? "hide the password"
                                  : "display the password"
                              }
                              onClick={() => setShowPassword((show) => !show)}
                              //onMouseDown={handleMouseDownPassword}
                              //onMouseUp={handleMouseUpPassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff sx={{ fontSize: "20px" }} />
                              ) : (
                                <Visibility sx={{ fontSize: "20px" }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  {/* SMS Consent Checkbox */}
                  <Box
                    sx={{ display: "flex", mt: 2, alignItems: "flex-start" }}
                  >
                    <Checkbox
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      disabled={isSubmitting}
                      sx={{ pt: 0, mt: "2px" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      By checking this box, you consent to receive transactional
                      SMS appointment reminders from <strong>FadePilot</strong>.
                      Reply STOP to opt out. Reply HELP for help. Standard
                      message and data rates may apply. Message frequency may
                      vary.
                    </Typography>
                  </Box>

                  {/* Privacy Policy Checkbox */}
                  <Box
                    sx={{ display: "flex", mt: 1.5, alignItems: "flex-start" }}
                  >
                    <Checkbox
                      checked={privacyConsent}
                      onChange={(e) => setPrivacyConsent(e.target.checked)}
                      disabled={isSubmitting}
                      sx={{ pt: 0, mt: "2px" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      I agree to the{" "}
                      <a
                        href="https://www.fadepilot.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "inherit" }}
                      >
                        Terms and Conditions
                      </a>{" "}
                      and{" "}
                      <a
                        href="https://www.fadepilot.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "inherit" }}
                      >
                        Privacy Policy
                      </a>
                      .
                    </Typography>
                  </Box>

                  <Button
                    color="primary"
                    variant="contained"
                    disabled={isSubmitting || !smsConsent || !privacyConsent}
                    sx={{ mt: 2 }}
                    onClick={() => {
                      handleSignUp();
                    }}
                  >
                    {!isSubmitting && "Signup"}
                    {isSubmitting && (
                      <CircularProgress
                        enableTrackSlot
                        size="30px"
                        aria-label="Loading…"
                        sx={{ color: "text.primary" }}
                      />
                    )}
                  </Button>

                  <Box
                    sx={{
                      display: isSubmitting ? "none" : "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mt: 3,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 300,
                      }}
                    >
                      Already have an account?
                    </Typography>

                    <Typography
                      variant="body2"
                      onClick={() => {
                        navigate("/login");
                      }}
                      sx={{
                        color: "text.primary",
                        fontWeight: 300,
                        ml: 1,
                        cursor: "pointer",
                        "&:hover": {
                          textDecoration: "underline",
                          textUnderlineOffset: "5px",
                        },
                      }}
                    >
                      Login
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid
          size={5}
          sx={{
            display: { xs: "none", lg: "flex" },
            padding: "15px",
          }}
        >
          <video
            width="100%"
            height="100%"
            controls={false}
            muted={true}
            autoPlay={true}
            loop={true}
            playsInline={true}
            preload="auto"
            poster="image"
            style={{
              objectFit: "cover",
              borderRadius: "15px",
            }}
          >
            <source src={myVideo} type="video/mp4" />
          </video>
        </Grid>
      </Grid>
    </>
  );
};

export default SignUp;
